// src/components/Instructions.tsx
import React from 'react';
import { Container, Typography, Box, Divider} from '@mui/material';
import { motion } from "framer-motion"

const Instructions: React.FC = () => {
  return (
    <div style={{ backgroundColor: '#001f3f', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '80px' }}>
      <Container maxWidth="md" style={{ backgroundColor: 'beige', color: '#021526', padding: '40px', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)' }}>
      <motion.div initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <Typography variant="h4" component="h1" gutterBottom style={{ color: "#001f3f", textAlign: "center" }}>
            Instructions
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Box mb={3}>
            <Typography variant="h4" component="h2" gutterBottom style={{ textAlign: "left" }}>
              MacOS Installation
            </Typography>
          </Box>

          <Box mb={3}>
            <Typography variant="h5" gutterBottom>
              Download and Install the App:
            </Typography>
            <Typography variant="body1" paragraph>
              1. Click on the <strong>Download</strong> button on the home page to download the app.
            </Typography>
          </Box>

          <Box mb={3}>
            <Typography variant="h5" gutterBottom>
              Installing Homebrew (Required for Dependencies):
            </Typography>
            <Typography variant="body1" paragraph>
              1. Open the <strong>Terminal</strong> app on your Mac.
            </Typography>
            <Typography variant="body1" paragraph>
              2. Copy and paste the following command into the terminal, then press <strong>Enter</strong> to install
              Homebrew:
            </Typography>
            <Box
              component="pre"
              style={{
                backgroundColor: "black",
                color: "white",
                padding: "10px",
                borderRadius: "5px",
                fontFamily: "monospace",
                overflowX: "auto",
              }}
            >
              /bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"
            </Box>
            <Typography variant="body1" paragraph>
              3. Follow the on-screen instructions in the terminal to complete the Homebrew installation. You may be
              prompted to enter your Mac password during this process.
            </Typography>
          </Box>

{/* Launching the App */}
<Box mb={3}>
  <Typography variant="h5" gutterBottom>
    Launching the App:
  </Typography>
  <Typography variant="body1" paragraph>
    1. Double-click the app to launch it. <strong>Note:</strong> The app may display a warning message when opened for the first time.
  </Typography>
  <Typography variant="body1" paragraph>
    2. If a warning message appears, click <strong>OK</strong>.
  </Typography>
</Box>

{/* Granting Permissions */}
<Box mb={3}>
  <Typography variant="h5" gutterBottom>
    Granting Permissions:
  </Typography>
  <Typography variant="body1" paragraph>
    1. Go to <strong>System Preferences</strong> &gt; <strong>Privacy & Security</strong>.
  </Typography>
  <Typography variant="body1" paragraph>
    2. Scroll down to find the <strong>Open Anyway</strong> option next to the app's name.
  </Typography>
  <Typography variant="body1" paragraph>
    3. Click <strong>Open Anyway</strong> and enter your Mac password if prompted.
  </Typography>
  <Typography variant="body1" paragraph>
    4. When prompted, allow <strong>Camera Permissions</strong> for the app.
  </Typography>
  <Typography variant="body1" paragraph>
    5. If you missed granting camera permissions during the first launch, go to <strong>System Preferences</strong> &gt; <strong>Privacy & Security</strong> &gt; <strong>Camera</strong>, and enable permissions for the app.
  </Typography>
</Box>

{/* Restarting and Running the App */}
<Box mb={3}>
  <Typography variant="h5" gutterBottom>
    Restarting and Running the App:
  </Typography>
  <Typography variant="body1" paragraph>
    1. After enabling camera permissions, restart the app if prompted.
  </Typography>
  <Typography variant="body1" paragraph>
    2. The app should now be functional. Follow the <strong>Using the App</strong> instructions below to get started.
  </Typography>
</Box>

{/* Separator */}
<Divider style={{ margin: '20px 0' }} />


{/* Windows Installation */}
<Box mb={3}>
  <Typography variant="h4" component="h2" gutterBottom style={{ textAlign: 'left' }}>
    Windows Installation
  </Typography>
</Box>

{/* Windows Instructions */}
<Box mb={3}>
  <Typography variant="h5" gutterBottom>
    Download and Install the App:
  </Typography>
  <Typography variant="body1" paragraph>
    1. Click on the <strong>Download</strong> button on the home page to download the app.
  </Typography>
  <Typography variant="body1" paragraph>
    2. Locate the downloaded file, unzip it, and extract the app to your desktop or any preferred location.
  </Typography>
</Box>


      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Launching the App:
        </Typography>
        <Typography variant="body1" paragraph>
          1. Double-click to run the app. <strong>Note:</strong> The app may take a few minutes to start up the first time.
        </Typography>
        <Typography variant="body1" paragraph>
          2. If you encounter a blue screen with the message <strong>"Windows Protected Your PC"</strong>, click on the <strong>More Info</strong> link, then select <strong>Run Anyway</strong> to bypass this warning.
        </Typography>
      </Box>

        {/* Separator */}
        <Divider style={{ margin: '20px 0' }} />

      <Box mb={3}>
        <Typography variant="h4" component="h2" gutterBottom>
          Using the App:
        </Typography>
        <Typography variant="body1" paragraph>
          Once the app has launched, you can either start detection immediately by clicking on the <strong>Start Detection</strong> button or upload an audio file first, which will act as an alert when you become distracted.
        </Typography>
        <Typography variant="body1" paragraph>
          To upload an audio file, click on the <strong>Select Audio File</strong> button, choose your preferred audio file, and then press <strong>Start Detection</strong>.
        </Typography>
      </Box>

      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Threshold Sliders and Options:
        </Typography>
        <Typography variant="body1" paragraph>
          The app includes various threshold sliders to help you fine-tune its sensitivity to distraction. Here’s a breakdown of each setting:
        </Typography>
        <Box ml={3}>
          <Typography variant="h6">Left Threshold and Right Threshold Sliders:</Typography>
          <Typography variant="body2" paragraph>
            These sliders control the <strong>eye position thresholds</strong>. When your eyes move beyond the defined threshold values (either too far left or too far right), the app considers you as "distracted."
          </Typography>
          <Typography variant="body2" paragraph>
            Adjust these sliders if the app is either too sensitive or not sensitive enough to your eye movements. Higher values make the app less sensitive to lateral eye movements, while lower values make it more sensitive.
          </Typography>

          <Typography variant="h6">Left Blink Threshold and Right Blink Threshold Sliders:</Typography>
          <Typography variant="body2" paragraph>
            These sliders control the <strong>blink detection sensitivity</strong>. When your blink pattern matches certain values, the app detects potential distraction.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Left Blink Threshold</strong> adjusts sensitivity for left eye blinks, and <strong>Right Blink Threshold</strong> does the same for the right eye.
          </Typography>
          <Typography variant="body2" paragraph>
            Adjust these sliders if the app frequently detects blinks as distraction. Lowering the threshold increases sensitivity to blinks, while raising it decreases sensitivity.
          </Typography>
        </Box>
      </Box>

      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Additional Options:
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Reset Thresholds Button:</strong> This button resets all threshold sliders to their default values. Use this if you’ve adjusted the sliders and want to return to the default settings.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Delay Time for Audio Alert (Optional):</strong> You can specify a delay time after which the audio alert will play once you’re detected as distracted. This feature allows you to set a buffer period before the alert starts.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>Alert Frequency (Optional):</strong> You can set how frequently the alert repeats if you remain distracted. Adjust this option based on your preference for more or fewer reminders.
        </Typography>
      </Box>

      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Stopping Detection:
        </Typography>
        <Typography variant="body1" paragraph>
          Click on the <strong>Stop Detection</strong> button or press <strong>‘q’</strong> on your keyboard to pause the app's detection and prevent any further alerts.
        </Typography>
      </Box>

      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          Viewing Distraction Metrics:
        </Typography>
        <Typography variant="body1" paragraph>
          When you stop detection, the app displays metrics summarizing your session, including:
        </Typography>
        <Box ml={3}>
          <Typography variant="body2" paragraph>
            <strong>Total Time Focused:</strong> The percentage of time you remained focused during the session.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Total Time Distracted:</strong> The percentage of time you were distracted.
          </Typography>
          <Typography variant="body2" paragraph>
            <strong>Total Elapsed Time:</strong> The total time spent in the detection session.
          </Typography>
        </Box>
        <Typography variant="body1" paragraph>
          These metrics can help you track your focus over time and adjust the thresholds as needed.
        </Typography>
      </Box>
      </motion.div>
    </Container>
    </div>
  );
};

export default Instructions;
